import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Search, X, FileText, FolderOpen, Building2, Users, Grid3x3, Download,
  Filter, Plus, MoreHorizontal, Eye, Edit, Mail, Archive, Check, FileSpreadsheet,
  FileEdit, Upload, Command
} from 'lucide-react';
import { Investor, InvestorFilters, PRODUCT_TYPES } from '@/types/investor';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { exportFilteredData } from '@/utils/investor/exportUtils';
import { generateImportTemplate, parseImportFile } from '@/utils/investor/importUtils';
import { getInitials, formatRelativeDate, formatDateTime } from '@/utils/investor/contactUtils';
import {
  getVisibleInvestors, computeStatistics, filterInvestors, sortInvestors, formatPeriodLabel,
  deriveDelegationLabel, getVisibleChannels, type ChannelView
} from '@/utils/investor/rosterUtils';
import { formatInvestorProducts } from '@/utils/investor/productUtils';
import { ContactCard } from './ContactCard';
import { SegmentChip } from '@/components/ui/segment-chip';
import { LW_THEME_CLASS } from './shared/InvestorThemeScope';

interface DashboardV2Props {
  investors: Investor[];
  onAddInvestor: (channel?: 'wholesale' | 'correspondent') => void;
  onViewInvestor: (investor: Investor, channel?: 'wholesale' | 'correspondent', defaultTab?: 'overview' | 'documents' | 'activity') => void;
  onEditInvestor: (investor: Investor, channel?: 'wholesale' | 'correspondent') => void;
  onArchiveInvestor: (investorId: string) => Promise<void> | void;
  onBulkArchiveInvestors?: (investorIds: string[]) => Promise<void> | void;
  onOpenArchivedInvestors?: () => void;
  /** Roster inline update. Legacy roster never wired inline editing (the cells are
   * read-only; editing happens via the slide-over / Add-Edit form), so v2 mirrors
   * that — this prop is accepted for contract parity but the roster does not fire it. */
  onUpdateInvestor?: (investor: Investor) => void;
  onBulkImport: (investors: Investor[]) => Promise<void> | void;
  readOnly?: boolean;
  organizationId?: string | null;
  onRefreshInvestor?: (investorId: string) => void;
  onOpenCommand?: () => void;
  filters: InvestorFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<InvestorFilters>>;
  sortField: keyof Investor;
  onSortFieldChange: React.Dispatch<React.SetStateAction<keyof Investor>>;
  sortDirection: 'asc' | 'desc';
  onSortDirectionChange: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
}

type StatusKind = 'Approved' | 'Pending' | 'Inactive' | string;

const statusPillKind = (status: StatusKind): 'ok' | 'warn' | 'neutral' | 'bad' =>
  status === 'Approved' ? 'ok' : status === 'Pending' ? 'warn' : status === 'Inactive' ? 'neutral' : 'bad';

/** lw-status-pill with dot. */
const StatusPill: React.FC<{ status: StatusKind; children?: React.ReactNode }> = ({ status, children }) => {
  const kind = statusPillKind(status);
  return (
    <span className={`lw-status-pill ${kind}`}>
      <span className={`lw-dot ${kind}`} />
      {children ?? status}
    </span>
  );
};

/** Approved-only channel tags (W gold / C green). */
const ChannelTags: React.FC<{ investor: Investor }> = ({ investor }) => {
  const tags: Array<{ key: 'w' | 'c'; label: string }> = [];
  if (investor.canDoWholesale && investor.status === 'Approved') {
    tags.push({ key: 'w', label: 'Wholesale' });
  }
  if (investor.canGoCorrespondent && investor.correspondentStatus === 'Approved') {
    tags.push({ key: 'c', label: 'Correspondent' });
  }
  if (tags.length === 0) {
    return <span className="lw-kv-value muted">—</span>;
  }
  return (
    <span className="lw-channel-tags">
      {tags.map((t) => (
        <span key={t.key} className={`lw-channel-tag ${t.key}`} title={`${t.label}: Approved`}>
          <Check size={11} strokeWidth={2.75} />
          {t.label}
        </span>
      ))}
    </span>
  );
};

/** Avatar + name with the existing ContactCard hover (email/phone). */
const AeCell: React.FC<{ name: string; email: string; phone: string; role: 'Wholesale AE' | 'Correspondent AE' | 'Account Executive'; company: string; channel: 'w' | 'c' | 'legacy'; }> = ({
  name, email, phone, role, company, channel
}) => {
  const bg = channel === 'w' ? 'var(--lw-w-color)' : channel === 'c' ? 'var(--lw-c-color)' : 'var(--lw-accent)';
  return (
    <ContactCard contact={{ name, email, phone, role, company }} contentClassName={LW_THEME_CLASS}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'default', minWidth: 0 }}>
        <span className="lw-avatar sm" style={{ background: bg }}>{getInitials(name)}</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--lw-fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{name}</span>
      </span>
    </ContactCard>
  );
};

/** EPO/EPD period — bold value + suffix, mirrors the legacy formatPeriod. */
const PeriodCell: React.FC<{ rule: Parameters<typeof formatPeriodLabel>[0] }> = ({ rule }) => {
  const parsed = formatPeriodLabel(rule);
  if (!parsed) {
    return <span className="lw-kv-value muted">N/A</span>;
  }
  return (
    <span style={{ fontSize: 12, color: 'var(--lw-fg-muted)' }}>
      {parsed.prefix}
      <b style={{ color: 'var(--lw-fg)', fontWeight: 600 }}>{parsed.value}</b> {parsed.suffix}
    </span>
  );
};

/** Internal infinite-scroll batch size (not user-facing). */
const ROSTER_BATCH = 25;

const defaultRosterFilters = (): InvestorFilters => ({
  search: '',
  status: 'All',
  productType: '',
  delegatedType: 'All',
  correspondentStatus: ['Approved', 'Pending'],
  wholesaleStatus: ['Approved', 'Pending'],
  wholesaleObIntegrated: 'All',
  correspondentObIntegrated: 'All',
  approvedDateRange: { startDate: '', endDate: '' },
  correspondentApprovedDateRange: { startDate: '', endDate: '' },
  createdDateRange: { startDate: '', endDate: '' },
  netWorthMin: '',
  netWorthMax: ''
});

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((value) => b.includes(value));

export const DashboardV2: React.FC<DashboardV2Props> = ({
  investors,
  onAddInvestor,
  onViewInvestor,
  onEditInvestor,
  onArchiveInvestor,
  onBulkArchiveInvestors,
  onOpenArchivedInvestors,
  onBulkImport,
  readOnly = false,
  onOpenCommand,
  filters,
  onFiltersChange: setFilters,
  sortField,
  onSortFieldChange: setSortField,
  sortDirection,
  onSortDirectionChange: setSortDirection
}) => {
  const [displayCount, setDisplayCount] = useState(ROSTER_BATCH);
  const [showFilters, setShowFilters] = useState(false);
  const firstStatusRef = useRef<HTMLButtonElement>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table'
  );
  const [channelView, setChannelView] = useState<ChannelView>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('investorChannelView');
      if (saved === 'wholesale' || saved === 'correspondent' || saved === 'both') {
        return saved;
      }
    }
    return 'both';
  });
  const [selectedInvestors, setSelectedInvestors] = useState<Set<string>>(new Set());
  const [showBulkArchiveConfirm, setShowBulkArchiveConfirm] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [bulkArchiveError, setBulkArchiveError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('investorChannelView', channelView);
  }, [channelView]);

  // Auto-switch to cards on small screens.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768 && viewMode === 'table') {
        setViewMode('cards');
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [viewMode]);

  const visibleInvestors = useMemo(() => getVisibleInvestors(investors, readOnly), [investors, readOnly]);
  const statistics = useMemo(() => computeStatistics(visibleInvestors), [visibleInvestors]);
  const filteredInvestors = useMemo(
    () => filterInvestors(visibleInvestors, filters, channelView),
    [visibleInvestors, filters, channelView]
  );
  const sortedInvestors = useMemo(
    () => sortInvestors(filteredInvestors, sortField, sortDirection, filters),
    [filteredInvestors, sortField, sortDirection, filters]
  );

  const displayedInvestors = sortedInvestors.slice(0, displayCount);
  const hasMore = displayCount < sortedInvestors.length;

  // Reset paging when the result set changes.
  useEffect(() => {
    setDisplayCount(ROSTER_BATCH);
  }, [filters, sortField, sortDirection, channelView]);

  // Infinite scroll — grow the window as the user nears the bottom. Table and
  // Cards each render their own scroll container, so re-attach on view changes.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop <= clientHeight + 200 && displayCount < sortedInvestors.length) {
        setDisplayCount((prev) => Math.min(prev + ROSTER_BATCH, sortedInvestors.length));
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [displayCount, sortedInvestors.length, viewMode]);

  const activeFilterChips = useMemo(() => {
    const defaults = defaultRosterFilters();
    const chips: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (filters.search) {
      chips.push({ key: 'search', label: `Search: ${filters.search}`, onClear: () => setFilters((prev) => ({ ...prev, search: '' })) });
    }
    if (filters.productType) {
      chips.push({ key: 'productType', label: `Product: ${filters.productType}`, onClear: () => setFilters((prev) => ({ ...prev, productType: '' })) });
    }
    if (!sameList(filters.wholesaleStatus, defaults.wholesaleStatus)) {
      chips.push({ key: 'wholesaleStatus', label: `Wholesale: ${filters.wholesaleStatus.join(', ') || 'All statuses'}`, onClear: () => setFilters((prev) => ({ ...prev, wholesaleStatus: defaults.wholesaleStatus })) });
    }
    if (!sameList(filters.correspondentStatus, defaults.correspondentStatus)) {
      chips.push({ key: 'correspondentStatus', label: `Correspondent: ${filters.correspondentStatus.join(', ') || 'All statuses'}`, onClear: () => setFilters((prev) => ({ ...prev, correspondentStatus: defaults.correspondentStatus })) });
    }
    if (filters.wholesaleObIntegrated !== defaults.wholesaleObIntegrated) {
      chips.push({ key: 'wholesaleObIntegrated', label: `Wholesale OB: ${filters.wholesaleObIntegrated}`, onClear: () => setFilters((prev) => ({ ...prev, wholesaleObIntegrated: 'All' })) });
    }
    if (filters.correspondentObIntegrated !== defaults.correspondentObIntegrated) {
      chips.push({ key: 'correspondentObIntegrated', label: `Correspondent OB: ${filters.correspondentObIntegrated}`, onClear: () => setFilters((prev) => ({ ...prev, correspondentObIntegrated: 'All' })) });
    }
    if (filters.delegatedType !== defaults.delegatedType) {
      chips.push({ key: 'delegatedType', label: `Delegation: ${filters.delegatedType}`, onClear: () => setFilters((prev) => ({ ...prev, delegatedType: 'All' })) });
    }
    if (filters.approvedDateRange.startDate || filters.approvedDateRange.endDate) {
      chips.push({ key: 'approvedDateRange', label: 'Wholesale approved date', onClear: () => setFilters((prev) => ({ ...prev, approvedDateRange: defaults.approvedDateRange })) });
    }
    if (filters.correspondentApprovedDateRange.startDate || filters.correspondentApprovedDateRange.endDate) {
      chips.push({ key: 'correspondentApprovedDateRange', label: 'Correspondent approved date', onClear: () => setFilters((prev) => ({ ...prev, correspondentApprovedDateRange: defaults.correspondentApprovedDateRange })) });
    }
    if (filters.createdDateRange.startDate || filters.createdDateRange.endDate) {
      chips.push({ key: 'createdDateRange', label: 'Created date', onClear: () => setFilters((prev) => ({ ...prev, createdDateRange: defaults.createdDateRange })) });
    }
    if (filters.netWorthMin || filters.netWorthMax) {
      chips.push({ key: 'netWorth', label: `Net worth: ${filters.netWorthMin || '0'}-${filters.netWorthMax || 'Any'}`, onClear: () => setFilters((prev) => ({ ...prev, netWorthMin: '', netWorthMax: '' })) });
    }
    return chips;
  }, [filters, setFilters]);

  const anyFilterApplied = activeFilterChips.length > 0;

  const handleSort = (field: keyof Investor) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'approvedAsOfDate' || field === 'correspondentApprovedAsOfDate' ? 'desc' : 'asc');
    }
  };

  const sortArrow = (field: keyof Investor) =>
    sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : '▾';

  const resetFilters = () => setFilters(defaultRosterFilters());

  const handleExport = async (format: 'csv' | 'excel') => {
    await exportFilteredData(sortedInvestors, format, channelView);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setIsImporting(true);
    setImportErrors([]);
    try {
      const { investors: importedInvestors, errors } = await parseImportFile(file);
      if (errors.length > 0) {
        setImportErrors(errors);
      } else {
        // Await so isImporting stays true (and the file input stays consumed)
        // until every row has actually been written or rejected.
        await onBulkImport(importedInvestors);
      }
    } catch (error) {
      setImportErrors([`Failed to import file: ${error instanceof Error ? error.message : 'Unknown error'}`]);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleSelectInvestor = (id: string) => {
    setSelectedInvestors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const allVisibleSelected = displayedInvestors.length > 0 && displayedInvestors.every((i) => selectedInvestors.has(i.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedInvestors(new Set());
    } else {
      setSelectedInvestors(new Set(displayedInvestors.map((i) => i.id)));
    }
  };

  const confirmBulkArchive = async () => {
    const ids = Array.from(selectedInvestors);
    setIsBulkArchiving(true);
    setBulkArchiveError(null);

    try {
      if (onBulkArchiveInvestors) {
        await onBulkArchiveInvestors(ids);
      } else {
        await Promise.all(ids.map((id) => onArchiveInvestor(id)));
      }
      setSelectedInvestors(new Set());
      setShowBulkArchiveConfirm(false);
    } catch (error) {
      setBulkArchiveError(error instanceof Error ? error.message : 'The selected investors could not be archived');
    } finally {
      setIsBulkArchiving(false);
    }
  };

  // Row click always opens the slide-over (detail view) — for editable AND read-only
  // users. Editing is reached from the slide-over ("Edit details") or the row kebab
  // (Edit), matching the approved v2 flow.
  // Round 6: which channel "versions" are visible per the active status filters.
  const channelVisibility = (investor: Investor) => getVisibleChannels(investor, filters);

  /** Channel the row click / kebab targets — prefer a visible channel, never a hidden inactive one. */
  const effectiveChannelFor = (investor: Investor): 'wholesale' | 'correspondent' => {
    if (channelView !== 'both') {
      return channelView;
    }
    const visible = channelVisibility(investor);
    if (visible.correspondent) {
      return 'correspondent';
    }
    if (visible.wholesale) {
      return 'wholesale';
    }
    return investor.canGoCorrespondent ? 'correspondent' : 'wholesale';
  };

  /** Roster products limited to the visible channels (so an inactive channel's products don't leak). */
  const visibleProductChannel = (investor: Investor): 'all' | 'wholesale' | 'correspondent' => {
    const visible = channelVisibility(investor);
    if (visible.wholesale && visible.correspondent) {
      return 'all';
    }
    if (visible.wholesale) {
      return 'wholesale';
    }
    if (visible.correspondent) {
      return 'correspondent';
    }
    return 'all';
  };

  /** Both-view EPO cell, scoped to the visible channel (no leak from a hidden channel). */
  const bothEpoRule = (investor: Investor) => {
    const visible = channelVisibility(investor);
    if (visible.wholesale && visible.correspondent) {
      return investor.correspondentEpoRule ?? investor.wholesaleEpoRule;
    }
    if (visible.correspondent) {
      return investor.correspondentEpoRule;
    }
    if (visible.wholesale) {
      return investor.wholesaleEpoRule;
    }
    return investor.correspondentEpoRule ?? investor.wholesaleEpoRule;
  };

  const handleRowClick = (investor: Investor) => {
    onViewInvestor(investor, effectiveChannelFor(investor));
  };

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>, investor: Investor) => {
    if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    event.preventDefault();
    handleRowClick(investor);
  };


  const obLabel = (canIntegrate?: boolean, hasIntegrated?: boolean): string => {
    if (hasIntegrated) {
      return 'Yes';
    }
    if (canIntegrate) {
      return 'See Lock Desk';
    }
    return 'No';
  };

  const delegationLabel = (investor: Investor): string => deriveDelegationLabel(investor);

  // Hover breakdown for the "Mixed" delegation label (item 6).
  const delegationTooltip = (investor: Investor): string | undefined => {
    const del = investor.delegation ?? { delegated: [], nonDelegated: [] };
    if (del.delegated.length === 0 && del.nonDelegated.length === 0) {
      return undefined;
    }
    const parts: string[] = [];
    if (del.delegated.length > 0) {
      parts.push(`Delegated: ${del.delegated.join(', ')}`);
    }
    if (del.nonDelegated.length > 0) {
      parts.push(`Non-Delegated: ${del.nonDelegated.join(', ')}`);
    }
    return parts.join(' · ');
  };

  const isW = channelView === 'wholesale';
  const isC = channelView === 'correspondent';
  const isBoth = channelView === 'both';

  const colCount = isW ? 7 : isC ? 9 : 8;
  const channelViewControl = (
    <div className="lw-roster-channels">
      <span className="lw-label">View:</span>
      <div className="lw-seg" role="group" aria-label="Investor channels">
        <button aria-pressed={isW} className={isW ? 'active' : ''} onClick={() => setChannelView('wholesale')}><Building2 size={13} /> Wholesale</button>
        <button aria-pressed={isC} className={isC ? 'active' : ''} onClick={() => setChannelView('correspondent')}><Users size={13} /> Correspondent</button>
        <button aria-pressed={isBoth} className={isBoth ? 'active' : ''} onClick={() => setChannelView('both')}><Grid3x3 size={13} /> Both</button>
      </div>
    </div>
  );

  return (
    <Dialog open={showFilters} onOpenChange={setShowFilters}>
    <div className="lw-page">
      <input ref={fileInputRef} type="file" onChange={handleFileSelect} accept=".xlsx,.xls,.csv" className="hidden" />

      {/* Import errors */}
      {importErrors.length > 0 && (
        <div className="lw-section-card" style={{ marginBottom: 'var(--lw-s-5)', borderColor: 'var(--lw-bad)' }}>
          <div className="lw-section-title" style={{ color: 'var(--lw-bad-fg)' }}>Import errors</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--lw-bad-fg)', fontSize: 'var(--lw-t-sm)' }}>
            {importErrors.map((err, i) => <li key={i}>{err}</li>)}
          </ul>
          <button type="button" className="lw-btn lw-btn-ghost sm" style={{ marginTop: 10 }} onClick={() => setImportErrors([])}>Dismiss</button>
        </div>
      )}

      {/* Header */}
      <div className="lw-page-header">
        <h1 className="lw-page-title">Manage Investors</h1>
        <p className="lw-page-subtitle">Investor relationships, channel approvals and documents.</p>
      </div>

      {/* Reuse Loan Center's summary chips and their selected-state treatment. */}
      <div className="grid grid-cols-1 gap-1.5 rounded-lg border bg-background p-1.5 mb-3 sm:grid-cols-3" role="group" aria-label="Investor summary">
        <SegmentChip label="Total Investors" value={String(statistics.totalInvestors)} sub="All channels" active={isBoth} accent="primary" onClick={() => setChannelView('both')} />
        <SegmentChip label="Approved Wholesale" value={String(statistics.totalApprovedWholesale)} sub="View wholesale relationships" active={isW} accent="warn" onClick={() => setChannelView('wholesale')} />
        <SegmentChip label="Approved Correspondent" value={String(statistics.totalApprovedCorrespondent)} sub="View correspondent relationships" active={isC} accent="ok" onClick={() => setChannelView('correspondent')} />
      </div>

      {/* Product distribution */}
      <div className="lw-card lw-distribution">
        <div className="lw-section-title" style={{ margin: 0 }}>Product Type Distribution</div>
        <div className="lw-distribution-row">
          {Object.entries(statistics.productTypeBreakdown).map(([type, count]) => {
            const active = filters.productType === type;
            return (
              <button
                key={type}
                aria-pressed={active}
                className={`lw-dist-pill ${active ? 'active' : ''}`}
                onClick={() => setFilters((prev) => ({ ...prev, productType: active ? '' : type }))}
              >
                <span className="lw-dist-pill-count">{count}</span>
                {type}
              </button>
            );
          })}
          {filters.productType && (
            <button className="lw-dist-clear" onClick={() => setFilters((prev) => ({ ...prev, productType: '' }))}>
              <X size={13} /> Clear
            </button>
          )}
          {Object.keys(statistics.productTypeBreakdown).length === 0 && (
            <span className="lw-meta-text">No product types found</span>
          )}
        </div>
      </div>

      {channelViewControl}

      {/* Toolbar */}
      <div className="lw-toolbar">
        <div className="lw-search">
          <Search size={16} style={{ color: 'var(--lw-fg-subtle)', flexShrink: 0 }} />
          <Input
            className="h-full min-w-0 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
            aria-label="Search investors"
            placeholder="Search investors, AE names, or emails…"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
          {filters.search && (
            <button className="lw-search-clear" aria-label="Clear search" onClick={(e) => {
 e.preventDefault(); setFilters((prev) => ({ ...prev, search: '' }));
}}>
              <X size={14} />
            </button>
          )}
        </div>

        <div className="lw-seg" role="group" aria-label="View mode">
          <button aria-pressed={viewMode === 'table'} className={viewMode === 'table' ? 'active' : ''} onClick={() => setViewMode('table')}><FileText size={13} /> Table</button>
          <button aria-pressed={viewMode === 'cards'} className={viewMode === 'cards' ? 'active' : ''} onClick={() => setViewMode('cards')}><FolderOpen size={13} /> Cards</button>
        </div>

        <DialogTrigger asChild>
        <button type="button" className={`lw-filter-btn ${showFilters || anyFilterApplied ? 'active' : ''}`}>
          <Filter size={14} /> Advanced Filters
        </button>
        </DialogTrigger>

        <div style={{ flex: 1 }} />

        <button type="button" className="lw-icon-btn" aria-label="Open command palette" title="Open command palette" onClick={onOpenCommand}>
          <Command size={16} />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="lw-icon-btn" aria-label="Data actions"><Download size={16} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={LW_THEME_CLASS}>
            <DropdownMenuItem onClick={() => handleExport('excel')}><FileSpreadsheet className="mr-2 h-4 w-4" /> Export as Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleExport('csv')}><FileText className="mr-2 h-4 w-4" /> Export as CSV</DropdownMenuItem>
            {!readOnly && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => fileInputRef.current?.click()} disabled={isImporting}>
                  <Upload className="mr-2 h-4 w-4" /> {isImporting ? 'Importing…' : 'Import from file'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => generateImportTemplate()}><FileEdit className="mr-2 h-4 w-4" /> Download template</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {!readOnly && (
          <>
            {onOpenArchivedInvestors && (
              <button type="button" className="lw-btn lw-btn-secondary" onClick={onOpenArchivedInvestors}><Archive size={14} /> Archived</button>
            )}
            <button type="button" className="lw-btn lw-btn-primary" onClick={() => onAddInvestor()}><Plus size={14} /> Add Investor</button>
          </>
        )}
      </div>

      {activeFilterChips.length > 0 && (
        <div className="lw-active-filters" aria-label="Active filters">
          {activeFilterChips.map((chip) => (
            <button key={chip.key} aria-label={`Reset ${chip.label} filter`} className="lw-filter-chip" onClick={chip.onClear}>
              {chip.label}
              <X size={12} />
            </button>
          ))}
          <button className="lw-filter-clear-all" onClick={resetFilters}>Reset to defaults</button>
        </div>
      )}

      {/* Advanced Filters modal */}
      <DialogContent
        className={`${LW_THEME_CLASS} bg-card flex max-h-[min(760px,calc(100dvh-2rem))] max-w-[960px] flex-col gap-4`}
        hideCloseButton
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          firstStatusRef.current?.focus();
        }}
      >
        <div className="flex shrink-0 items-start justify-between gap-4">
          <div>
            <DialogTitle className="lw-h3" style={{ margin: 0 }}>Advanced Filters</DialogTitle>
            <DialogDescription className="lw-meta-text" style={{ margin: '4px 0 0' }}>Refine the investor roster without changing the table layout.</DialogDescription>
          </div>
          <DialogClose asChild>
            <button type="button" className="lw-icon-btn" aria-label="Close filters"><X size={16} /></button>
          </DialogClose>
        </div>
          <div className="lw-investor-filter-fields min-h-0 overflow-y-auto pr-1">
            {(isW || isBoth) && (
              <div>
                <label id="wholesale-status-label" htmlFor="wholesale-status" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Wholesale Status</label>
                <MultiSelect
                  ref={firstStatusRef}
                  autoFocus
                  id="wholesale-status" aria-labelledby="wholesale-status-label" aria-describedby="wholesale-status-help"
                  options={[{ label: 'Approved', value: 'Approved' }, { label: 'Pending', value: 'Pending' }, { label: 'Inactive', value: 'Inactive' }]}
                  selected={filters.wholesaleStatus}
                  onChange={(selected) => setFilters((prev) => ({ ...prev, wholesaleStatus: selected }))}
                  placeholder="All statuses"
                  className="border-input bg-card text-foreground focus:ring-ring focus:border-ring"
                  contentClassName={LW_THEME_CLASS}
                />
                <p id="wholesale-status-help" className="lw-meta-text" style={{ margin: '4px 0 0' }}>Leave empty to include all statuses.</p>
              </div>
            )}
            {(isW || isBoth) && (
              <div>
                <label id="wholesale-ob-label" htmlFor="wholesale-ob" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Wholesale OB Integrated</label>
                <select id="wholesale-ob" className="lw-perpage" style={{ width: '100%' }} value={filters.wholesaleObIntegrated} onChange={(e) => setFilters((prev) => ({ ...prev, wholesaleObIntegrated: e.target.value as 'All' | 'Yes' | 'No' }))}>
                  <option value="All">All</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </div>
            )}
            {(isC || isBoth) && (
              <div>
                <label id="correspondent-status-label" htmlFor="correspondent-status" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Correspondent Status</label>
                <MultiSelect
                  ref={isC ? firstStatusRef : undefined}
                  autoFocus={isC}
                  id="correspondent-status" aria-labelledby="correspondent-status-label" aria-describedby="correspondent-status-help"
                  options={[{ label: 'Approved', value: 'Approved' }, { label: 'Pending', value: 'Pending' }, { label: 'Inactive', value: 'Inactive' }]}
                  selected={filters.correspondentStatus}
                  onChange={(selected) => setFilters((prev) => ({ ...prev, correspondentStatus: selected }))}
                  placeholder="All statuses"
                  className="border-input bg-card text-foreground focus:ring-ring focus:border-ring"
                  contentClassName={LW_THEME_CLASS}
                />
                <p id="correspondent-status-help" className="lw-meta-text" style={{ margin: '4px 0 0' }}>Leave empty to include all statuses.</p>
              </div>
            )}
            {(isC || isBoth) && (
              <div>
                <label id="correspondent-ob-label" htmlFor="correspondent-ob" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Correspondent OB Integrated</label>
                <select id="correspondent-ob" className="lw-perpage" style={{ width: '100%' }} value={filters.correspondentObIntegrated} onChange={(e) => setFilters((prev) => ({ ...prev, correspondentObIntegrated: e.target.value as 'All' | 'Yes' | 'No' }))}>
                  <option value="All">All</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </div>
            )}
            <div>
              <label id="product-type-label" htmlFor="product-type" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Product Type</label>
              <select id="product-type" className="lw-perpage" style={{ width: '100%' }} value={filters.productType} onChange={(e) => setFilters((prev) => ({ ...prev, productType: e.target.value }))}>
                <option value="">All Product Types</option>
                {[...PRODUCT_TYPES].map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            {isC && (
              <div>
                <label id="delegated-type-label" htmlFor="delegated-type" className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Delegated Type</label>
                <select id="delegated-type" className="lw-perpage" style={{ width: '100%' }} value={filters.delegatedType} onChange={(e) => setFilters((prev) => ({ ...prev, delegatedType: e.target.value as InvestorFilters['delegatedType'] }))}>
                  <option value="All">All Types</option><option value="Delegated">Delegated</option><option value="Non-Delegated">Non-Delegated</option><option value="Delegated/Non-Delegated">Delegated/Non-Delegated</option>
                </select>
              </div>
            )}
            {(isW || isBoth) && (
              <fieldset className="min-w-0">
                <legend className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Wholesale Approved Date</legend>
                <div className="lw-investor-filter-range">
                  <Input type="date" className="lw-input" aria-label="Wholesale approved from" value={filters.approvedDateRange.startDate} onChange={(e) => setFilters((prev) => ({ ...prev, approvedDateRange: { ...prev.approvedDateRange, startDate: e.target.value } }))} />
                  <Input type="date" className="lw-input" aria-label="Wholesale approved to" value={filters.approvedDateRange.endDate} onChange={(e) => setFilters((prev) => ({ ...prev, approvedDateRange: { ...prev.approvedDateRange, endDate: e.target.value } }))} />
                </div>
              </fieldset>
            )}
            {(isC || isBoth) && (
              <fieldset className="min-w-0">
                <legend className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Correspondent Approved Date</legend>
                <div className="lw-investor-filter-range">
                  <Input type="date" className="lw-input" aria-label="Correspondent approved from" value={filters.correspondentApprovedDateRange.startDate} onChange={(e) => setFilters((prev) => ({ ...prev, correspondentApprovedDateRange: { ...prev.correspondentApprovedDateRange, startDate: e.target.value } }))} />
                  <Input type="date" className="lw-input" aria-label="Correspondent approved to" value={filters.correspondentApprovedDateRange.endDate} onChange={(e) => setFilters((prev) => ({ ...prev, correspondentApprovedDateRange: { ...prev.correspondentApprovedDateRange, endDate: e.target.value } }))} />
                </div>
              </fieldset>
            )}
            <fieldset className="min-w-0">
              <legend className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Created Date</legend>
              <div className="lw-investor-filter-range">
                <Input type="date" className="lw-input" aria-label="Created from" value={filters.createdDateRange.startDate} onChange={(e) => setFilters((prev) => ({ ...prev, createdDateRange: { ...prev.createdDateRange, startDate: e.target.value } }))} />
                <Input type="date" className="lw-input" aria-label="Created to" value={filters.createdDateRange.endDate} onChange={(e) => setFilters((prev) => ({ ...prev, createdDateRange: { ...prev.createdDateRange, endDate: e.target.value } }))} />
              </div>
            </fieldset>
            <fieldset className="min-w-0">
              <legend className="lw-label" style={{ display: 'block', marginBottom: 4 }}>Net Worth ($)</legend>
              <div className="lw-investor-filter-range">
                <Input type="number" className="lw-input" placeholder="Min" aria-label="Net worth min" value={filters.netWorthMin} onChange={(e) => setFilters((prev) => ({ ...prev, netWorthMin: e.target.value }))} />
                <Input type="number" className="lw-input" placeholder="Max" aria-label="Net worth max" value={filters.netWorthMax} onChange={(e) => setFilters((prev) => ({ ...prev, netWorthMax: e.target.value }))} />
              </div>
            </fieldset>
          </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
          <span className="lw-meta-text">{anyFilterApplied ? 'Custom filters applied' : 'Default filters applied'}</span>
          <div className="flex flex-wrap gap-2">
            <DialogClose asChild>
              <button type="button" className="lw-btn lw-btn-secondary sm">Done</button>
            </DialogClose>
            <button type="button" className="lw-btn sm" onClick={resetFilters} disabled={!anyFilterApplied}><X size={14} /> Reset to defaults</button>
          </div>
        </div>
      </DialogContent>

      {/* Selection / bulk action bar */}
      {selectedInvestors.size > 0 && (
        <div className="lw-card lw-card-pad-sm" style={{ marginBottom: 'var(--lw-s-4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--lw-s-3)', flexWrap: 'wrap', borderColor: 'var(--lw-accent)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lw-s-3)', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, color: 'var(--lw-fg)', fontSize: 'var(--lw-t-sm)' }}>{selectedInvestors.size} selected</span>
            {!readOnly && (
              <button type="button" className="lw-btn lw-btn-danger sm" onClick={() => setShowBulkArchiveConfirm(true)}><Archive size={14} /> Archive</button>
            )}
            <button type="button" className="lw-btn lw-btn-secondary sm" onClick={async () => {
              const chosen = investors.filter((inv) => selectedInvestors.has(inv.id));
              await exportFilteredData(chosen, 'excel', channelView);
            }}><Download size={14} /> Export</button>
          </div>
          <button type="button" className="lw-btn lw-btn-ghost sm" onClick={() => setSelectedInvestors(new Set())}>Clear selection</button>
        </div>
      )}

      {/* Meta row */}
      <div className="lw-meta-row">
        <div className="lw-meta-text">
          Showing <b style={{ color: 'var(--lw-fg)', fontWeight: 600 }}>{displayedInvestors.length}</b> of {sortedInvestors.length} investors
          {sortedInvestors.length !== visibleInvestors.length && <> (filtered from {visibleInvestors.length} total)</>}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'cards' ? (
        <div ref={scrollContainerRef} className="lw-card-grid" style={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto', padding: 0 }}>
          {displayedInvestors.map((investor) => (
            <article
              key={investor.id}
              className={`lw-inv-card ${selectedInvestors.has(investor.id) ? 'selected' : ''}`}
              onClick={() => handleRowClick(investor)}
            >
              <div className="lw-inv-card-head">
                <input type="checkbox" className="lw-cbx" aria-label={`Select ${investor.investor}`} checked={selectedInvestors.has(investor.id)} onClick={(event) => event.stopPropagation()} onChange={() => toggleSelectInvestor(investor.id)} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <button className="lw-inv-card-name text-left" onClick={(event) => {
 event.stopPropagation(); handleRowClick(investor);
}}>{investor.investor}</button>
                  <div className="lw-inv-card-products-text">{formatInvestorProducts(investor, visibleProductChannel(investor)) || 'No products set'}</div>
                </div>
                <ChannelTags investor={investor} />
              </div>
              <div className="lw-inv-card-divider" />
              <div className="lw-inv-card-body">
                {/* Round 6: only render the channel "version" that's visible per the active status filters. */}
                {(() => {
                  const visible = channelVisibility(investor);
                  return (
                    <>
                      {visible.wholesale && <div><div className="lw-label">Wholesale</div><div style={{ marginTop: 4 }}><StatusPill status={investor.status} /></div></div>}
                      {visible.wholesale && <div><div className="lw-label">W OB</div><div style={{ marginTop: 4 }}><span className="lw-kv-value">{obLabel(investor.wholesaleCanIntegrateOptimalBlue, investor.wholesaleHasIntegratedOptimalBlue)}</span></div></div>}
                      {visible.correspondent && <div><div className="lw-label">Correspondent</div><div style={{ marginTop: 4 }}><StatusPill status={investor.correspondentStatus} /></div></div>}
                      {visible.correspondent && <div><div className="lw-label">Delegation</div><div style={{ marginTop: 4 }}><span className="lw-kv-value">{delegationLabel(investor)}</span></div></div>}
                    </>
                  );
                })()}
              </div>
            </article>
          ))}
          {displayedInvestors.length === 0 && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 0' }}>
              <FolderOpen size={40} style={{ margin: '0 auto 12px', color: 'var(--lw-fg-subtle)' }} />
              <p style={{ fontWeight: 600, color: 'var(--lw-fg)' }}>No investors found</p>
              <p className="lw-meta-text">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      ) : (
        <div className="lw-card lw-tbl-card">
          <div ref={scrollContainerRef} className="lw-tbl-wrap" tabIndex={0} role="region" aria-label="Investor roster" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <table className="lw-tbl">
              <thead>
                <tr>
                  <th className="checkbox-cell"><input type="checkbox" className="lw-cbx" checked={allVisibleSelected} onChange={toggleSelectAll} aria-label="Select all" /></th>
                  <th className={`sortable ${sortField === 'investor' ? 'active' : ''}`} onClick={() => handleSort('investor')}>Investor <span className="lw-sort-arrow">{sortArrow('investor')}</span></th>
                  {isW && <th>Wholesale AE</th>}
                  {isW && <th className={`sortable ${sortField === 'status' ? 'active' : ''}`} onClick={() => handleSort('status')}>Status <span className="lw-sort-arrow">{sortArrow('status')}</span></th>}
                  {isW && <th className={`sortable ${sortField === 'wholesaleLenderPaidComp' ? 'active' : ''}`} onClick={() => handleSort('wholesaleLenderPaidComp')}>LPC % <span className="lw-sort-arrow">{sortArrow('wholesaleLenderPaidComp')}</span></th>}
                  {isC && <th>Correspondent AE</th>}
                  {isC && <th className={`sortable ${sortField === 'correspondentStatus' ? 'active' : ''}`} onClick={() => handleSort('correspondentStatus')}>C Status <span className="lw-sort-arrow">{sortArrow('correspondentStatus')}</span></th>}
                  {isC && <th className={`sortable ${sortField === 'delegatedType' ? 'active' : ''}`} onClick={() => handleSort('delegatedType')}>Delegation Level <span className="lw-sort-arrow">{sortArrow('delegatedType')}</span></th>}
                  {isC && <th>OB</th>}
                  {isC && <th className={`sortable ${sortField === 'correspondentEpoRule' ? 'active' : ''}`} onClick={() => handleSort('correspondentEpoRule')}>EPO Period <span className="lw-sort-arrow">{sortArrow('correspondentEpoRule')}</span></th>}
                  {isBoth && <th>Channels</th>}
                  {isBoth && <th>AE Name</th>}
                  {isBoth && <th>EPO</th>}
                  {isBoth && <th className={`sortable ${sortField === 'delegatedType' ? 'active' : ''}`} onClick={() => handleSort('delegatedType')}>Delegation Level <span className="lw-sort-arrow">{sortArrow('delegatedType')}</span></th>}
                  <th className={`sortable ${sortField === 'updatedAt' ? 'active' : ''}`} onClick={() => handleSort('updatedAt')}>Last Updated <span className="lw-sort-arrow">{sortArrow('updatedAt')}</span></th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {displayedInvestors.map((investor) => (
                  <tr
                    key={investor.id}
                    className={selectedInvestors.has(investor.id) ? 'selected lw-clickable-row' : 'lw-clickable-row'}
                    tabIndex={0}
                    onClick={() => handleRowClick(investor)}
                    onKeyDown={(event) => handleRowKeyDown(event, investor)}
                  >
                    <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="lw-cbx" checked={selectedInvestors.has(investor.id)} onChange={() => toggleSelectInvestor(investor.id)} aria-label={`Select ${investor.investor}`} />
                    </td>
                    <td>
                      <div className="investor-name">
                        <div style={{ minWidth: 0 }}>
                          <div className="investor-name-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button type="button" className="text-left" onClick={(event) => {
                              event.stopPropagation();
                              handleRowClick(investor);
                            }}>{investor.investor}</button>
                            {investor.draftStatus === 'draft' && (
                              <span className="lw-status-pill warn" style={{ height: 20 }}><FileEdit size={11} /> Draft</span>
                            )}
                          </div>
                          <div className="investor-name-sub">{(channelView === 'both' ? formatInvestorProducts(investor, visibleProductChannel(investor)) : formatInvestorProducts(investor)) || '—'}</div>
                        </div>
                      </div>
                    </td>

                    {isW && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {(investor.wholesaleAeName || investor.aeName)
                          ? <AeCell name={investor.wholesaleAeName || investor.aeName} email={investor.wholesaleAeEmail || investor.aeEmail} phone={investor.wholesaleAePhone || investor.aePhone} role="Wholesale AE" company={investor.investor} channel="w" />
                          : <span className="lw-kv-value muted">—</span>}
                      </td>
                    )}
                    {isW && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <StatusPill status={investor.status} />
                      </td>
                    )}
                    {isW && (
                      <td style={{ fontWeight: 600, color: 'var(--lw-fg)' }}>
                        {typeof investor.wholesaleLenderPaidComp === 'number' ? `${investor.wholesaleLenderPaidComp}%` : <span className="lw-kv-value muted">—</span>}
                      </td>
                    )}

                    {isC && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {(investor.correspondentAeName || investor.aeName)
                          ? <AeCell name={investor.correspondentAeName || investor.aeName} email={investor.correspondentAeEmail || investor.aeEmail} phone={investor.correspondentAePhone || investor.aePhone} role="Correspondent AE" company={investor.investor} channel="c" />
                          : <span className="lw-kv-value muted">—</span>}
                      </td>
                    )}
                    {isC && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <StatusPill status={investor.correspondentStatus} />
                      </td>
                    )}
                    {isC && <td style={{ fontSize: 'var(--lw-t-sm)' }} title={delegationTooltip(investor)}>{delegationLabel(investor)}</td>}
                    {isC && <td><span className="lw-kv-value">{obLabel(investor.correspondentCanIntegrateOptimalBlue, investor.correspondentHasIntegratedOptimalBlue)}</span></td>}
                    {isC && <td><PeriodCell rule={investor.correspondentEpoRule} /></td>}

                    {isBoth && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <ChannelTags investor={investor} />
                      </td>
                    )}
                    {isBoth && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {/* One AE row per VISIBLE channel (participates AND status passes its filter — Round 6).
                            Participating-but-unassigned keeps the pill row with an em-dash; hidden/inactive renders nothing. */}
                        {(() => {
                          const visible = channelVisibility(investor);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {visible.wholesale && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span className="lw-channel-mark w">W</span>
                                  {(investor.wholesaleAeName || investor.aeName)
                                    ? <AeCell name={investor.wholesaleAeName || investor.aeName} email={investor.wholesaleAeEmail || investor.aeEmail || ''} phone={investor.wholesaleAePhone || investor.aePhone || ''} role="Wholesale AE" company={investor.investor} channel="w" />
                                    : <span className="lw-kv-value muted">—</span>}
                                </div>
                              )}
                              {visible.correspondent && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span className="lw-channel-mark c">C</span>
                                  {(investor.correspondentAeName || investor.aeName)
                                    ? <AeCell name={investor.correspondentAeName || investor.aeName} email={investor.correspondentAeEmail || investor.aeEmail || ''} phone={investor.correspondentAePhone || investor.aePhone || ''} role="Correspondent AE" company={investor.investor} channel="c" />
                                    : <span className="lw-kv-value muted">—</span>}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {isBoth && <td><PeriodCell rule={bothEpoRule(investor)} /></td>}
                    {isBoth && <td style={{ fontSize: 'var(--lw-t-sm)' }} title={delegationTooltip(investor)}>{delegationLabel(investor)}</td>}

                    <td style={{ fontSize: 'var(--lw-t-sm)', color: 'var(--lw-fg-muted)' }} title={formatDateTime(investor.updatedAt)}>{formatRelativeDate(investor.updatedAt)}</td>

                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="lw-icon-btn" style={{ width: 32, height: 32 }} aria-label="Row actions"><MoreHorizontal size={16} /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className={LW_THEME_CLASS}>
                          <DropdownMenuItem onClick={() => onViewInvestor(investor, effectiveChannelFor(investor))}><Eye className="mr-2 h-4 w-4" /> View Details</DropdownMenuItem>
                          {!readOnly && (
                            <>
                              <DropdownMenuItem onClick={() => onEditInvestor(investor, effectiveChannelFor(investor))}><Edit className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                              {investor.aeEmail && <DropdownMenuItem onClick={() => {
 window.location.href = `mailto:${investor.aeEmail}`;
}}><Mail className="mr-2 h-4 w-4" /> Email</DropdownMenuItem>}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={async () => {
 // eslint-disable-next-line no-alert -- single-row archive keeps the compact confirm UX; bulk archive uses the in-app modal
 if (window.confirm(`Archive ${investor.investor}? It will be removed from active investor lists while linked records are retained.`)) {
 try {
 await onArchiveInvestor(investor.id);
} catch {
 // The page-level handler presents the actionable error toast.
}
}
}}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {displayedInvestors.length === 0 && (
                  <tr><td colSpan={colCount} style={{ textAlign: 'center', padding: '48px 0', color: 'var(--lw-fg-muted)' }}>No investors found matching your criteria.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Load more */}
      {hasMore && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--lw-s-5)' }}>
          <button type="button" className="lw-btn lw-btn-secondary" onClick={() => setDisplayCount((prev) => Math.min(prev + ROSTER_BATCH, sortedInvestors.length))}>
            Load more ({sortedInvestors.length - displayedInvestors.length} more)
          </button>
        </div>
      )}

      {/* Bulk archive confirm */}
      {showBulkArchiveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--lw-bg-overlay)', display: 'grid', placeItems: 'center', zIndex: 60 }} onClick={() => {
 if (!isBulkArchiving) {
 setShowBulkArchiveConfirm(false);
}
}}>
          <div className="lw-card lw-card-pad" style={{ maxWidth: 420, width: '90%' }} role="dialog" aria-modal="true" aria-labelledby="bulk-archive-title" onClick={(e) => e.stopPropagation()}>
            <h3 id="bulk-archive-title" className="lw-h3" style={{ marginTop: 0 }}>Archive selected investors?</h3>
            <p className="lw-body" style={{ color: 'var(--lw-fg-muted)' }}>
              Archive {selectedInvestors.size} selected investor{selectedInvestors.size !== 1 ? 's' : ''}? They will be removed from active lists while linked records and audit history are retained.
            </p>
            {bulkArchiveError && <p role="alert" className="lw-body" style={{ color: 'var(--lw-bad-fg)' }}>{bulkArchiveError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--lw-s-3)', marginTop: 'var(--lw-s-4)' }}>
              <button type="button" className="lw-btn lw-btn-secondary" disabled={isBulkArchiving} onClick={() => setShowBulkArchiveConfirm(false)}>Cancel</button>
              <button type="button" className="lw-btn lw-btn-danger" disabled={isBulkArchiving} onClick={confirmBulkArchive}><Archive size={14} /> {isBulkArchiving ? 'Archiving…' : `Archive ${selectedInvestors.size}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Dialog>
  );
};

export default DashboardV2;
